import type { AclConfigHelpers, SubjectConditions } from '../../types/acl-types.ts';
import type { AppConfig } from '../../types/app-config-type.ts';
import { allowFilterableSubject } from './allow-filterable-subject';
import { allowRead } from './utils/allow-read.ts';

export const applyAclForDashboard = (helpers: AclConfigHelpers, options: AppConfig) => {
    const { allow } = helpers;
    const { salesRepIds, canReadGrossMargin } = options;

    allowRead(helpers, 'Stats', {
        total_margin: canReadGrossMargin,
    });

    if (!salesRepIds) {
        allow('list', 'Stats');
        allow('list', 'Insights');
    } else if (salesRepIds.length > 0) {
        allow('list', 'Stats', {
            salesRepIds,
        });
        allow('list', 'Insights', {
            salesRepIds,
        });
    } else {
        allow('list', 'Insights');
    }
};

const applyAclForCustomers = (helpers: AclConfigHelpers, options: AppConfig) => {
    const { allow, restrict } = helpers;
    const {
        salesRepIds,
        canReadCreditLimit,
        canReadCustomerContacts,
        canReadShipToAddresses,
        canReadGrossMargin,
        canFilterCustomerBySalesRepType,
        canFilterCustomerBySalesLocation,
        canFilterCustomerByCustomerClass,
        canFilterCustomerBySalesRegion,
        enablePlatoStatus,
    } = options;

    allowFilterableSubject({ allow, restrict }, 'Customer', {
        conditions: salesRepIds?.length ? { salesRepIds } : undefined,
    });

    if (!canFilterCustomerBySalesRepType) {
        restrict('filter', 'CustomerFilter', ['salesRepTypes']);
    }

    if (!canFilterCustomerBySalesLocation) {
        restrict('filter', 'CustomerFilter', ['companyIds']);
    }

    if (!canFilterCustomerByCustomerClass) {
        restrict('filter', 'CustomerFilter', ['customerClasses']);
    }

    if (!canFilterCustomerBySalesRegion) {
        restrict('filter', 'CustomerFilter', ['salesRegions']);
    }

    if (!enablePlatoStatus) {
        restrict('filter', 'CustomerFilter', ['statuses']);
    }

    allowRead(helpers, 'CustomerFinancial', {
        gross_margin_ttm: canReadGrossMargin,
        gross_margin_ttm_percentage: canReadGrossMargin,
        gross_margin_ytd_current: canReadGrossMargin,
        gross_margin_ytd_percentage: canReadGrossMargin,
    });

    if (canReadCreditLimit) {
        allow('read', 'CustomerCreditLimit');
    }

    if (canReadCustomerContacts) {
        allow('read', 'CustomerContacts');
    }

    if (canReadShipToAddresses) {
        allow('read', 'ShipToAddresses');
    }

    // Customer activity feed
    allow('filter', 'CustomerActivityFeedFilter');
    allow('manage', 'Note');
};

const applyAclForQuotesAndOrders = (helpers: AclConfigHelpers, options: AppConfig) => {
    const { allow, restrict } = helpers;
    const {
        salesRepIds,
        canReadQuoteOrderReleaseDate,
        canReleaseQuoteOrder,
        canDeleteQuoteOrder,
        canReadLineItemRebate,
        canReadGrossMargin,
        canReadLineItemPurchasePrice,
        canFilterQuoteOrderByStorageLocation,
        canFilterQuoteOrderBySalesLocation,
        canFilterQuoteOrderByStatus,
    } = options;

    // Quotes and orders
    allowFilterableSubject({ allow, restrict }, 'QuoteOrder', {
        conditions: salesRepIds?.length
            ? {
                  salesRepIds,
              }
            : undefined,
        extraFields: {
            released_date: canReadQuoteOrderReleaseDate,
            total_margin: canReadGrossMargin,
        },
    });
    allowRead(helpers, 'QuoteOrderLineItem', {
        gross_margin: canReadGrossMargin,
        encrypted_purchase_price: canReadLineItemPurchasePrice,
        discount_percent: canReadLineItemRebate,
    });

    if (!canFilterQuoteOrderByStorageLocation) {
        restrict('filter', 'QuoteOrderFilter', ['storageLocationIds']);
    }

    if (!canFilterQuoteOrderBySalesLocation) {
        restrict('filter', 'QuoteOrderFilter', ['companyIds']);
    }

    if (!canFilterQuoteOrderByStatus) {
        restrict('filter', 'QuoteOrderFilter', ['orderStatus']);
    }

    if (canReleaseQuoteOrder) {
        allow('release', 'QuoteOrder');
    }

    if (canDeleteQuoteOrder) {
        allow('delete', 'QuoteOrder');
    }

    allow('list', 'ProductQuoteOrder');
    allowRead(helpers, 'ProductQuoteOrder', {
        product_gross_margin_in_order: canReadGrossMargin,
    });
};

const applyAclForDrafts = (helpers: AclConfigHelpers, options: AppConfig) => {
    const { allow, restrict } = helpers;
    const {
        canUpdateTransactionShippingType,
        canUpdateTransactionDeliveryDate,
        canUpdateTransactionDueDate,
        canUpdateTransactionDocumentType,
        canUpdateLineItemPrice,
        canUpdateLineItemRebate,
        canUpdateLineItemStorageLocation,
        transactionDraftTypes,
        canReadLineItemRebate,
        canReadGrossMargin,
        canReadTransactionGrossMargin,
        canReadLineItemPurchasePrice,
        canUpdateTransactionStorageLocation,
        canReadTransactionStorageLocation,
        userIds,
    } = options;

    // Write backs
    const writeConditions = transactionDraftTypes
        ? ({ type: transactionDraftTypes } satisfies SubjectConditions<'SalesDraft'>)
        : undefined;

    allow('create', 'SalesDraft', writeConditions);
    allow('update', 'SalesDraft', writeConditions);
    allow('delete', 'SalesDraft');
    allow(
        'list',
        'SalesDraft',
        userIds
            ? {
                  users: userIds,
              }
            : undefined,
    );

    allowRead(helpers, 'SalesDraft', {
        storage_id: canReadTransactionStorageLocation,
    });

    allow('filter', 'SalesDraftFilter');
    allow('update', 'SalesDraftLineItem');

    if (!canUpdateTransactionShippingType) {
        restrict('update', 'SalesDraftLineItem', ['shipping_type_id']);
        restrict('update', 'SalesDraft', ['shipping_type_id']);
    }

    if (!canUpdateLineItemPrice) {
        restrict('update', 'SalesDraftLineItem', ['price']);
    }

    if (!canUpdateLineItemRebate) {
        restrict('update', 'SalesDraftLineItem', ['rebate']);
    }

    if (!canUpdateLineItemStorageLocation) {
        restrict('update', 'SalesDraftLineItem', ['storage_id']);
    }

    if (!canUpdateTransactionStorageLocation) {
        restrict('update', 'SalesDraft', ['storage_id']);
    }

    if (!canUpdateTransactionDeliveryDate) {
        restrict('update', 'SalesDraft', ['delivery_date']);
    }

    if (!canUpdateTransactionDueDate) {
        restrict('update', 'SalesDraft', ['due_date']);
    }

    if (!canUpdateTransactionDeliveryDate) {
        restrict('update', 'SalesDraft', ['delivery_date']);
    }

    if (!canUpdateTransactionDueDate) {
        restrict('update', 'SalesDraft', ['due_date']);
    }

    if (!canUpdateTransactionDocumentType) {
        restrict('update', 'SalesDraft', ['external.document_type']);
    }

    allowRead(helpers, 'SalesDraftLineItem', {
        gross_margin: canReadGrossMargin && canReadTransactionGrossMargin,
        rebate: canReadLineItemRebate,
        'external.encoded_price': canReadLineItemPurchasePrice,
        encrypted_purchase_price: canReadLineItemPurchasePrice,
    });
};

const applyAclForSalesReport = (helpers: AclConfigHelpers, options: AppConfig) => {
    const { allow, restrict } = helpers;
    const {
        salesRepIds,
        canReadGrossMargin,
        canExportSalesReport,
        canFilterSalesReportByStorageLocation,
        canFilterSalesReportByIndustry,
        canFilterSalesReportByProductGroup,
        canFilterSalesReportBySupplier,
        canFilterSalesReportByClerk,
        canFilterSalesReportBySalesRep,
    } = options;

    if (!salesRepIds) {
        allowFilterableSubject({ allow, restrict }, 'SalesReport');
    } else if (salesRepIds.length > 0) {
        allow('list', 'SalesReport', { salesRep: salesRepIds });
        allow('filter', 'SalesReportFilter');
        allow('read', 'SalesReport');
    } else {
        allowFilterableSubject({ allow, restrict }, 'SalesReport');
    }

    // Restrict clerk filter if user lacks permission OR if no clerk data exists
    if (!canFilterSalesReportByClerk) {
        restrict('filter', 'SalesReportFilter', ['clerk']);
    }

    // Only restrict sales rep filter if mode is 'none'
    if (canFilterSalesReportBySalesRep === 'none') {
        restrict('filter', 'SalesReportFilter', ['salesRep']);
    }

    if (!canFilterSalesReportByStorageLocation) {
        restrict('filter', 'SalesReportFilter', ['storageLocation']);
    }

    if (!canFilterSalesReportByIndustry) {
        restrict('filter', 'SalesReportFilter', ['industry']);
    }

    if (!canFilterSalesReportByProductGroup) {
        restrict('filter', 'SalesReportFilter', ['productGroup']);
    }

    if (!canFilterSalesReportBySupplier) {
        restrict('filter', 'SalesReportFilter', ['supplier']);
    }

    if (!canReadGrossMargin) {
        restrict('read', 'SalesReport', [
            'gross_margin_current',
            'gross_margin_last_period',
            'gross_margin_delta_amount',
            'gross_margin_period_3',
            'gross_margin_period_4',
            'gross_margin_period_5',
        ]);
    }

    // Allow or restrict export action based on canExportSalesReport permission
    if (canExportSalesReport) {
        allow('export', 'SalesReport');
    }
};

const applyAclForTasks = ({ allow }: AclConfigHelpers, { userIds }: AppConfig) => {
    allow(
        'list',
        'Task',
        userIds && {
            userIds,
        },
    );
    allow('filter', 'TaskFilter');
};

const applyAclForEvents = ({ allow }: AclConfigHelpers, { userId }: AppConfig) => {
    allow('create', 'CustomerEvent');
    allow('list', 'CustomerEvent', {
        userId,
    });
};

export const applyAclConfig = (helpers: AclConfigHelpers, options: AppConfig): AppConfig => {
    const { canManageTransactionDraft, enableCustomerEventPlanning } = options;

    applyAclForDashboard(helpers, options);
    applyAclForCustomers(helpers, options);
    applyAclForQuotesAndOrders(helpers, options);
    applyAclForSalesReport(helpers, options);
    applyAclForTasks(helpers, options);

    if (canManageTransactionDraft) {
        applyAclForDrafts(helpers, options);
    }

    if (enableCustomerEventPlanning) {
        applyAclForEvents(helpers, options);
    }

    return options;
};
